import { useEffect, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { api } from '@/api/client';
import { useAuth } from '@/lib/AuthContext';
import { usePageMeta } from '@/lib/pageMeta';
import { User, Coffee, Heart, ArrowLeft } from 'lucide-react';
import ReviewCard from '@/components/coffee/ReviewCard';
import MokaPotBadge from '@/components/coffee/MokaPotBadge';
import { Skeleton } from '@/components/ui/skeleton';

// Public, read-only view of another member's profile and reviews. The current
// user's own profile lives at /my, which additionally carries the edit and
// account controls — a self-visit redirects there.
export default function UserProfile() {
  const { id } = useParams();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [coffeeMap, setCoffeeMap] = useState({});
  const [likeCounts, setLikeCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const isSelf = !!user && user.id === id;

  useEffect(() => {
    if (isSelf) return;
    let cancelled = false;
    setLoading(true);
    setNotFound(false);

    // Reads go through the public_profiles projection, so this only ever
    // exposes the columns meant for other people to see — name, avatar, bio,
    // badge and counts, never the email.
    api.entities.User.get(id)
      .then(async (found) => {
        if (cancelled) return;
        if (!found) {
          setNotFound(true);
          setLoading(false);
          return;
        }
        setProfile(found);
        const revs = await api.entities.Review.filter({ reviewer_id: id }, '-created_date', 50);
        if (cancelled) return;
        setReviews(revs);
        if (revs.length > 0) {
          const [coffees, likes] = await Promise.all([
            api.entities.Coffee.filter({ id: [...new Set(revs.map((r) => r.coffee_id))] }),
            api.entities.ReviewLike.filter({ review_id: revs.map((r) => r.id) }),
          ]);
          if (cancelled) return;
          const map = {};
          coffees.forEach((c) => (map[c.id] = c));
          setCoffeeMap(map);
          const counts = {};
          likes.forEach((l) => { counts[l.review_id] = (counts[l.review_id] || 0) + 1; });
          setLikeCounts(counts);
        } else {
          setCoffeeMap({});
          setLikeCounts({});
        }
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setNotFound(true);
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [id, isSelf]);

  const displayName = profile?.display_name || profile?.full_name || 'Coffee Lover';
  const reviewCount = profile?.review_count ?? reviews.length;

  // Hooks run before the early returns below. A self-visit bounces to /my and
  // a missing member answers 200 through the SPA rewrite, so both say noindex.
  usePageMeta({
    title: profile ? `${displayName} | Signature Coffee Note` : undefined,
    description: profile
      ? `${displayName} has reviewed ${reviewCount} signature ${reviewCount === 1 ? 'coffee' : 'coffees'} on Signature Coffee Note.`
      : undefined,
    image: profile?.avatar_url,
    index: isSelf || notFound ? false : undefined,
  });

  if (isSelf) return <Navigate to="/my" replace />;

  if (notFound) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-4">
        <p className="text-muted-foreground">User not found.</p>
        <Link to="/" className="text-primary text-sm hover:underline">Back to home</Link>
      </div>
    );
  }

  const totalLikes = reviews.reduce((s, r) => s + (likeCounts[r.id] || 0), 0);

  return (
    <div className="min-h-screen">
      {/* Profile header */}
      <div className="px-5 pt-14 pb-8 md:pt-20 md:pb-12" style={{ background: 'linear-gradient(135deg, #2c1a0e 0%, #3d2210 40%, #1a0f08 100%)' }}>
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full flex items-center justify-center border-2 shrink-0 overflow-hidden" style={{ borderColor: '#c9a96e', background: 'rgba(201,169,110,0.1)' }}>
              {profile?.avatar_url ? (
                <img src={profile.avatar_url} alt={displayName} className="w-full h-full object-cover" />
              ) : (
                <User size={28} style={{ color: '#c9a96e' }} strokeWidth={1.5} />
              )}
            </div>
            <div className="min-w-0">
              <h1 className="font-heading font-semibold text-2xl flex items-center gap-2" style={{ color: '#f0e6d3' }}>
                <span className="truncate">{loading ? '...' : displayName}</span>
                {!loading && <MokaPotBadge reviewCount={reviewCount} size={22} />}
              </h1>
              {profile?.bio && (
                <p className="text-xs mt-1" style={{ color: 'rgba(240,230,211,0.5)' }}>
                  {profile.bio}
                </p>
              )}
            </div>
          </div>

          {/* Stats */}
          <div className="flex gap-4 mt-6">
            <StatChip icon={Coffee} label="Reviews" value={loading ? '–' : reviewCount} />
            <StatChip icon={Heart} label="Likes" value={loading ? '–' : totalLikes} />
          </div>
        </div>
      </div>

      <div className="px-4 py-6 space-y-4 max-w-5xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft size={14} />
          Back
        </Link>

        <h2 className="font-heading font-semibold text-base">Reviews</h2>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {Array(4).fill(0).map((_, i) => <Skeleton key={i} className="h-32 rounded-2xl" />)}
          </div>
        ) : reviews.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-6 items-stretch">
            {reviews.map((review) => {
              const coffee = coffeeMap[review.coffee_id];
              return (
                <div key={review.id} className="flex flex-col h-full">
                  {coffee && (
                    <div className="mb-2 shrink-0 bg-muted rounded-xl px-3 py-1.5 border border-border">
                      <Link
                        to={`/coffee/${coffee.id}`}
                        className="text-xs font-semibold text-primary hover:underline"
                      >
                        {coffee.name} · {coffee.cafe_name}
                      </Link>
                    </div>
                  )}
                  <ReviewCard
                    review={review}
                    user={user}
                    initialLikeCount={likeCounts[review.id] || 0}
                    isOwner={false}
                    showTranslateUI={true}
                    reviewerReviewCount={reviewCount}
                    reviewerAvatarUrl={profile?.avatar_url}
                    linkToProfile={false}
                  />
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-16 border border-dashed border-border rounded-2xl">
            <Coffee size={36} className="mx-auto mb-3 text-muted-foreground opacity-40" strokeWidth={1} />
            <p className="text-sm text-muted-foreground">No reviews yet.</p>
          </div>
        )}
      </div>
    </div>
  );
}

function StatChip({ icon: IconComp, label, value }) {
  return (
    <div className="rounded-xl px-4 py-2.5 flex items-center gap-2" style={{ background: 'rgba(201,169,110,0.1)', border: '1px solid rgba(201,169,110,0.2)' }}>
      <IconComp size={15} style={{ color: '#c9a96e' }} strokeWidth={1.5} />
      <div>
        <p className="font-bold text-base leading-tight" style={{ color: '#f0e6d3' }}>{value}</p>
        <p className="text-[10px]" style={{ color: 'rgba(240,230,211,0.5)' }}>{label}</p>
      </div>
    </div>
  );
}
